import { datadogRum } from '@datadog/browser-rum'
import { useCallbackEvent } from '@dni-react-hooks/use-callback-event'
import { getCkoApplePayDialogMountId } from '@payments/lib/checkout-pay/ckoMountTarget'
import { useEffect, useRef, useState } from 'react'
import { PaymentCancelledError } from '../core/checkoutPayFlowController'
import { type ApplePayDialogContext } from './useApplePayDialogActions'

const MOUNT_TIMEOUT_MS = 30_000

export interface UseApplePayDialogParams {
  context: ApplePayDialogContext
  mountApplePayButton: (context: ApplePayDialogContext, containerId: string) => Promise<void>
  awaitAndExecutePayment: (context: ApplePayDialogContext) => Promise<void>
  cancelInFlight: (reason: string) => void
  /** 付款失敗（非取消）時呼叫；未提供則顯示 dialog 內通用錯誤訊息。 */
  onError?: (error: unknown) => void
}

export const useApplePayDialog = (props: UseApplePayDialogParams) => {
  const { context, mountApplePayButton, awaitAndExecutePayment, cancelInFlight, onError } = props
  const [isMounting, setIsMounting] = useState<boolean>(true)
  const [hasError, setHasError] = useState<boolean>(false)
  const [retryNonce, setRetryNonce] = useState<number>(0)
  const containerId = getCkoApplePayDialogMountId(context.paymentTransactionId)
  // 用 ref 讀最新 onError，避免非穩定 callback 進 effect deps 造成重新 mount
  const onErrorRef = useRef(onError)
  onErrorRef.current = onError

  const retry = useCallbackEvent(() => {
    setHasError(false)
    setRetryNonce(nonce => nonce + 1)
  })

  useEffect(function runApplePayFlow() {
    let isActive = true
    let timeoutId: ReturnType<typeof setTimeout> | undefined
    setIsMounting(true)
    setHasError(false)

    const start = async () => {
      try {
        await Promise.race([
          mountApplePayButton(context, containerId),
          new Promise<never>((_, reject) => {
            timeoutId = setTimeout(
              () => reject(new Error('[useApplePayDialog] mount timed out')),
              MOUNT_TIMEOUT_MS,
            )
          }),
        ])
      }
      catch (mountFailure) {
        if (!isActive || mountFailure instanceof PaymentCancelledError) return
        datadogRum.addError(mountFailure instanceof Error ? mountFailure : new Error(String(mountFailure)), {
          function: 'useApplePayDialog.runApplePayFlow.mount',
          paymentTransactionId: context.paymentTransactionId,
          orderHash: context.orderHash,
        })
        onErrorRef.current?.(mountFailure)
        setHasError(true)
        return
      }
      finally {
        clearTimeout(timeoutId)
        if (isActive) setIsMounting(false)
      }

      try {
        await awaitAndExecutePayment(context)
      }
      catch (executionFailure) {
        if (!isActive || executionFailure instanceof PaymentCancelledError) return
        datadogRum.addError(executionFailure instanceof Error ? executionFailure : new Error(String(executionFailure)), {
          function: 'useApplePayDialog.runApplePayFlow.execution',
          paymentTransactionId: context.paymentTransactionId,
          orderHash: context.orderHash,
        })
        onErrorRef.current?.(executionFailure)
        setHasError(true)
      }
    }
    start()

    return () => {
      isActive = false
      clearTimeout(timeoutId)
    }
  }, [context, containerId, mountApplePayButton, awaitAndExecutePayment, retryNonce])

  // 獨立於流程 effect：只在 dialog 真正卸載時取消。retryNonce 變動重跑流程 effect 不該觸發此取消，
  // 否則 execution 階段失敗後按重新再試，會誤拆掉剛掛好的 SDK component。
  useEffect(function cancelOnDialogUnmount() {
    return () => {
      cancelInFlight('dialog unmounting')
    }
  }, [cancelInFlight])

  return {
    isMounting,
    hasError,
    containerId,
    cancel: cancelInFlight,
    retry,
  }
}
